import * as R from 'ramda';

import { Task } from '@atom/types/task';
import {
  TaskValidations,
  WorkOrderDetailType,
  WorkValidations,
} from '@atom/types/work';

import { isNilOrEmpty } from './validationUtilities';

// Validates task against required multi asset rules
//  - requireAtLeastOneMultiAsset: true --> checks whether task has at least one asset
//  - requiredMultiAssetSchemaIds: [ids] --> checks whether task contains at least one asset of each specified schemaId
export const isTaskMultiAssetInvalid = (
  workOrderDetail: WorkOrderDetailType,
  task: Task,
): boolean => {
  const isMissingAtLeastOne =
    task?.requireAtLeastOneMultiAsset && isNilOrEmpty(task?.assetIds);

  const assets = R.pathOr([], ['assetIds'], task).reduce(
    (innerAcc, assetId) => {
      const asset = R.pathOr(null, ['multiAssets', assetId], workOrderDetail);
      return isNilOrEmpty(asset) ? innerAcc : [...innerAcc, asset];
    },
    [],
  );

  const uniqueSchemaIds = new Set(assets.map(asset => asset.schemaId));
  const uniqueAndRequireMatch = R.pathOr(
    [],
    ['requiredMultiAssetSchemaIds'],
    task,
  ).reduce((innerAcc, schemaId) => {
    return uniqueSchemaIds.has(schemaId) ? innerAcc : false;
  }, true);

  const isMissingSpecificAssets =
    !isNilOrEmpty(task?.requiredMultiAssetSchemaIds) && !uniqueAndRequireMatch;

  return isMissingAtLeastOne || isMissingSpecificAssets;
};

export const convertInvalidFieldsToSet = (
  taskValidations: TaskValidations,
): TaskValidations => {
  return R.keys(taskValidations).reduce((acc, taskId) => {
    return {
      ...acc,
      [taskId]: {
        ...taskValidations[taskId],
        invalidTaskFields: new Set(
          R.pathOr([], [taskId, 'invalidTaskFields'], taskValidations),
        ),
      },
    };
  }, {});
};

// Work validation errors are passed in a very specific place within the GQL errors array
// This method pulls them out, given the proper error code of 422.
export const getValidationsFromError = (error: any) => {
  const errorsArray = R.pathOr([], ['networkError', 'result', 'errors'], error);

  if (isNilOrEmpty(errorsArray)) {
    return {};
  }

  const validationError = R.find(
    item =>
      item?.extensions?.code === 422 && R.has('errorBody')(item?.extensions),
    errorsArray,
  );

  const mappedTaskValidations = convertInvalidFieldsToSet(
    R.pathOr(
      {},
      ['extensions', 'errorBody', 'taskValidations'],
      validationError,
    ),
  );

  return mappedTaskValidations;
};

// Removes property from object its value is empty
const cleanEmptyProperties = (map: any): any => {
  return R.keys(map).reduce((acc, property) => {
    return isNilOrEmpty(map[property])
      ? acc
      : { ...acc, [property]: map[property] };
  }, {});
};

// This method ensures that only tasks that have at least one type of error
// are present in the the work validation.
export const cleanWorkValidations = (
  workValidations: WorkValidations,
): WorkValidations => {
  // Ensure invalidAssets and invalidForms maps to not include
  // assets or forms with no errors
  const cleanAssetsAndForms: TaskValidations = R.keys(
    workValidations.taskValidations,
  ).reduce((acc, taskId) => {
    return {
      ...acc,
      [taskId]: {
        ...workValidations.taskValidations[taskId],
        invalidAssets: cleanEmptyProperties(
          workValidations.taskValidations[taskId].invalidAssets,
        ),
        invalidForms: cleanEmptyProperties(
          workValidations.taskValidations[taskId].invalidForms,
        ),
      },
    };
  }, {});

  // Removes tasks that do not have at least one error present
  const cleanTaskValidations = R.keys(cleanAssetsAndForms).reduce(
    (acc, taskId) => {
      const validations = cleanAssetsAndForms[taskId];
      const hasError = R.any(R.equals(true), [
        validations?.missingRequiredAsset,
        validations?.missingRequiredLocation,
        R.pathOr(new Set([]), ['invalidTaskFields'], validations).size > 0,
        !isNilOrEmpty(validations?.invalidAssets),
        !isNilOrEmpty(validations?.invalidForms),
      ]);

      return hasError ? { ...acc, [taskId]: validations } : acc;
    },
    {},
  );

  return {
    ...workValidations,
    taskValidations: cleanTaskValidations,
  };
};

export const getWorkAssetErrorAttributes = (
  assetId: string,
  taskId: string,
  workValidations: WorkValidations,
): { [attributeGroupId: string]: string[] } => {
  if (isNilOrEmpty(assetId)) {
    return {};
  }

  const assetErrors = R.pathOr(
    [],
    ['taskValidations', taskId, 'invalidAssets', assetId],
    workValidations,
  );

  return assetErrors.reduce((acc, assetError) => {
    return {
      ...acc,
      [assetError.attributeGroupId]: [
        ...(acc[assetError.attributeGroupId] || []),
        assetError.attributeId,
      ],
    };
  }, {});
};

// Finds what task the given form Instance has errors on. Given the
// various ways to access a form instance, the task it comes from is not always known
export const getTaskIdByFormInstanceId = (
  formInstanceId: string,
  workValidations: WorkValidations,
): string => {
  // @ts-ignore
  return (
    R.find(
      taskId =>
        R.has(formInstanceId)(
          workValidations?.taskValidations[taskId].invalidForms,
        ),
      R.keys(workValidations.taskValidations),
    ) || null
  );
};

export const hasFormAssetAttributeError = (
  formInstanceId: string,
  workValidations: WorkValidations,
): boolean => {
  return !isNilOrEmpty(
    getTaskIdByFormInstanceId(formInstanceId, workValidations),
  );
};
